import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AuthHeader } from 'components';
import PasswordResetCreateNew from 'components/Auth/PasswordResetCreateNew';
import { validation } from 'lib';
import { routeConstants } from 'routes';
import { ErrorScreen } from 'screens/Error';
import { clearRequest, selectAPIRequestErrors, selectAPIRequestStatus, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { App, resetPasswordRequest, selectForgotPasswordIdentity, selectForgotPasswordVerifyToken } from 'store/App';
import { IStore } from 'store/types';
import { IApiErrorPayloadWithCode, RequestStatus } from 'types';

const validationSchema = Yup.object().shape({
  password: validation.passwordValidationSchema,
});

const PasswordCreateNew: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const identity = useSelector(selectForgotPasswordIdentity());
  const token = useSelector(selectForgotPasswordVerifyToken())?.token;
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, App.RESET_PASSWORD_REQUEST));
  const apiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, App.RESET_PASSWORD_REQUEST)) as IApiErrorPayloadWithCode;
  const passwordResetStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, App.RESET_PASSWORD_REQUEST));

  if (!identity) return <ErrorScreen to={routeConstants.public.invites.login} />;

  const { values, errors, touched, isValid, dirty, handleSubmit, getFieldProps } = useFormik({
    initialValues: {
      password: '',
      token,
    },
    onSubmit: () => {
      dispatch(resetPasswordRequest(identity.id, values, 'Successfully reset password'));
    },
    validateOnBlur: true,
    enableReinitialize: true,
    validationSchema,
  });

  const validateField = (field: 'password') => Boolean(touched[field] && errors[field]);

  const authHeader = <AuthHeader title="Create New Password" subtitle="Thank you for confirming your identity. Enter a new password to log in to Kickfin." />;

  const onNewForm = useCallback(() => {
    dispatch(clearRequest(App.RESET_PASSWORD_REQUEST));
  }, [dispatch]);

  useEffect(() => {
    if (passwordResetStatus === RequestStatus.SUCCESS) {
      // redirect to invite login after successful password reset
      history.push(routeConstants.public.invites.login);
    }
  }, [passwordResetStatus, history]);

  return (
    <PasswordResetCreateNew
      validateField={validateField}
      authHeader={authHeader}
      formId={App.RESET_PASSWORD_REQUEST}
      errors={errors}
      getFieldProps={getFieldProps}
      isLoading={isLoading}
      dirty={dirty}
      isValid={isValid}
      apiErrors={apiErrors}
      handleSubmit={handleSubmit}
      onNewForm={onNewForm}
    />
  );
};

export default PasswordCreateNew;
